import { CommonModule, DatePipe } from '@angular/common';
import { Component, signal, Type } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDataGridCellRenderer } from 'shared/abstract/base-data-grid-cell-renderer';
import { SimpleTooltipComponent } from 'shared/components/simple-tooltip/simple-tooltip.component';
import { TooltipDirective } from 'shared/directives/tooltip.directive';

export interface DateCellRendererParams {
    format?: string;
    timezone?: string;
    valueFormat?: (value: any) => string;
    tooltip?: {
        text?: string | ((value: any) => string);
        component?: Type<any>;
        componentData?: any | ((value: any) => any);
        position?: 'top' | 'bottom' | 'left' | 'right';
        trigger?: 'click' | 'hover';
    };
}

@Component({
    selector: 'app-data-grid-date-cell-renderer',
    templateUrl: './data-grid-date-cell-renderer.component.html',
    styleUrls: ['./data-grid-date-cell-renderer.component.scss'],
    imports: [DatePipe, CommonModule, TranslateModule, TooltipDirective],
})
export class DataGridDateCellRendererComponent extends BaseDataGridCellRenderer {
    public date!: string | number;
    public format!: string;
    public timezone?: string;

    protected displayValue = signal<string>('');
    protected useCustomFormat = signal<boolean>(false);

    protected hasTooltip = signal<boolean>(false);
    protected tooltipComponent = signal<Type<any>>(SimpleTooltipComponent);
    protected tooltipData = signal<any>({});
    protected tooltipPosition = signal<'top' | 'bottom' | 'left' | 'right'>('right');
    protected tooltipTrigger = signal<'click' | 'hover'>('hover');

    public onDataChange() {
        if (!this.column || !this.data || !this.column.dataKey) {
            return;
        }

        const rendererParams: DateCellRendererParams = this.column.customCellRendererData ?? {};
        const data = this.data as any;
        const cellValue = data[this.column.dataKey];

        this.date = cellValue;
        this.format = rendererParams.format ?? 'yyyy-MM-dd';
        this.timezone = rendererParams.timezone ?? undefined;

        if (rendererParams.valueFormat) {
            this.useCustomFormat.set(true);
            this.displayValue.set(rendererParams.valueFormat.call(this, cellValue));
        } else {
            this.useCustomFormat.set(false);
            this.displayValue.set('');
        }

        this.setupTooltip(rendererParams, cellValue);
    }

    private setupTooltip(rendererParams: DateCellRendererParams, cellValue: any): void {
        const tooltipConfig = rendererParams.tooltip;

        if (!tooltipConfig) {
            this.hasTooltip.set(false);
            return;
        }

        this.hasTooltip.set(true);
        this.tooltipPosition.set(tooltipConfig.position ?? 'right');
        this.tooltipTrigger.set(tooltipConfig.trigger ?? 'hover');

        if (tooltipConfig.component) {
            this.tooltipComponent.set(tooltipConfig.component);

            if (tooltipConfig.componentData) {
                this.tooltipData.set(
                    typeof tooltipConfig.componentData === 'function'
                        ? tooltipConfig.componentData.call(this, cellValue)
                        : tooltipConfig.componentData,
                );
            }
        } else if (tooltipConfig.text) {
            this.tooltipComponent.set(SimpleTooltipComponent);
            this.tooltipData.set(typeof tooltipConfig.text === 'function' ? tooltipConfig.text.call(this, cellValue) : tooltipConfig.text);
        }
    }
}
